import React, { useState } from 'react';
import { Activity, Play, Wind, Droplets } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';

type AeratorAnalysisType = 'fluid_torque' | 'shaft_critical_speed' | 'battery_drain';

const AeratorAnalysis: React.FC = () => {
    const [analysisType, setAnalysisType] = useState<AeratorAnalysisType>('fluid_torque');
    const [params, setParams] = useState<{ [key: string]: string | number }>({});
    const [result, setResult] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const analysisOptions = [
        { value: 'fluid_torque', label: 'Fluid Drag Torque' },
        { value: 'shaft_critical_speed', label: 'Shaft Critical Speed' },
        { value: 'battery_drain', label: 'Battery Life Estimate' },
    ];

    const getFields = (type: AeratorAnalysisType) => {
        switch (type) {
            case 'fluid_torque': return [
                { key: 'rpm', label: 'Speed (RPM)', type: 'number' },
                { key: 'viscosity_cp', label: 'Viscosity (cP)', type: 'number', placeholder: 'Water=1, Milk=3, Oil=100' },
                { key: 'diameter_mm', label: 'Impeller Diameter (mm)', type: 'number' },
                { key: 'fluid_density_kgm3', label: 'Fluid Density (kg/m3)', type: 'number', placeholder: 'Water=1000' }
            ];
            case 'shaft_critical_speed': return [
                { key: 'length_mm', label: 'Shaft Length (mm)', type: 'number' },
                { key: 'diameter_mm', label: 'Shaft Diameter (mm)', type: 'number' },
                { key: 'material', label: 'Material', type: 'select', options: ['steel', 'aluminum', 'titanium'] },
                { key: 'rpm', label: 'Operating Speed (RPM)', type: 'number' }
            ];
            case 'battery_drain': return [
                { key: 'battery_capacity_mah', label: 'Capacity (mAh)', type: 'number' },
                { key: 'current_draw_ma', label: 'Current Draw (mA)', type: 'number' },
                { key: 'derating', label: 'Derating Factor (0-1)', type: 'number', placeholder: '0.8' }
            ];
            default: return [];
        }
    };

    const handleCalculate = async () => {
        setLoading(true);
        try {
            const payload = {
                analysis_type: analysisType,
                parameters: Object.entries(params).reduce((acc, [k, v]) => ({
                    ...acc,
                    [k]: typeof v === 'string' && !isNaN(parseFloat(v)) ? parseFloat(v) : v
                }), {})
            };
            const data = await authenticatedFetchJSON('/api/v1/aerator/analyze', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            setResult(data);
        } catch {
            setResult({ error: "Analysis failed. Check backend connection." });
        } finally {
            setLoading(false);
        }
    };

    const currentFields = getFields(analysisType);

    return (
        <div className="bg-card border border-border p-6 rounded-lg shadow-sm">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Wind className="text-blue-500" /> Aerator Physics
                </h2>
                <select
                    value={analysisType}
                    onChange={(e) => {
                        setAnalysisType(e.target.value as AeratorAnalysisType);
                        setParams({});
                        setResult(null);
                    }}
                    className="bg-background border border-border rounded px-2 py-1 text-sm"
                >
                    {analysisOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    {currentFields.map(field => (
                        <div key={field.key}>
                            <label className="block text-xs font-medium mb-1 text-text-secondary">{field.label}</label>
                            {field.type === 'select' ? (
                                <select
                                    className="w-full bg-background border border-border rounded p-2 text-sm"
                                    value={params[field.key] || ''}
                                    onChange={e => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                >
                                    <option value="">Select...</option>
                                    {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            ) : (
                                <input
                                    type="number"
                                    className="w-full bg-background border border-border rounded p-2 text-sm"
                                    value={params[field.key] || ''}
                                    onChange={e => setParams(prev => ({ ...prev, [field.key]: e.target.value }))}
                                    placeholder={field.placeholder}
                                />
                            )}
                        </div>
                    ))}

                    <button
                        onClick={handleCalculate}
                        disabled={loading}
                        className="w-full bg-primary text-white py-2 rounded flex justify-center items-center gap-2 hover:bg-primary/90 mt-4"
                    >
                        {loading ? <Activity className="animate-spin" /> : <Play size={16} />}
                        Analyze Model
                    </button>
                </div>

                <div className="bg-muted/20 border border-border rounded p-4 h-full min-h-[200px]">
                    <h3 className="text-sm font-semibold text-text-secondary mb-2 uppercase tracking-wide">Analysis Results</h3>
                    {result ? (
                        <div className="space-y-2">
                            {Object.entries(result).map(([k, v]) => (
                                <div key={k} className="flex justify-between items-center border-b border-border/50 pb-1 last:border-0">
                                    <span className="text-sm text-text-secondary capitalize">{k.replace(/_/g, ' ')}</span>
                                    <span className="text-sm font-mono font-bold text-text-primary">
                                        {typeof v === 'number' ? v.toFixed(4) : String(v)}
                                    </span>
                                </div>
                            ))}

                            {analysisType === 'shaft_critical_speed' && result.critical_speed_rpm && (
                                <div className={`mt-4 p-3 rounded text-sm ${Math.abs(result.critical_speed_rpm - (Number(params.rpm) || 0)) < (0.2 * result.critical_speed_rpm)
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-green-100 text-green-800'
                                    }`}>
                                    {Math.abs(result.critical_speed_rpm - (Number(params.rpm) || 0)) < (0.2 * result.critical_speed_rpm)
                                        ? "⚠️ Warning: Operating speed is close to critical speed (Resonance Risk!)"
                                        : "✓ Safe: Margin from critical speed is sufficient."}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-text-secondary opacity-50">
                            <Droplets size={32} className="mb-2" />
                            <p className="text-xs">Enter fluid & mechanical parameters to simulate.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AeratorAnalysis;
